import { useState, useEffect, useCallback } from 'react';
import { Download, X, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

let deferredPromptGlobal = null;
let installListeners = [];
let userRequestedUpdate = false;

// Global event system so other components can trigger install
export const onInstallAvailable = (cb) => {
  installListeners.push(cb);
  if (deferredPromptGlobal) cb(true);
  return () => { installListeners = installListeners.filter(l => l !== cb); };
};

export const triggerInstall = async (userType = 'unknown') => {
  if (!deferredPromptGlobal) return false;
  deferredPromptGlobal.prompt();
  const { outcome } = await deferredPromptGlobal.userChoice;
  if (outcome === 'accepted') {
    deferredPromptGlobal = null;
    installListeners.forEach(cb => cb(false));
    // Track installation with user type
    try { await axios.post(`${API}/settings/track-install`, { user_type: userType }); } catch {}
  }
  return outcome === 'accepted';
};

export const isInstallAvailable = () => !!deferredPromptGlobal;

const PWAInstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newWorker, setNewWorker] = useState(null);

  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('pwa-dismissed');

    const handler = (e) => {
      e.preventDefault();
      deferredPromptGlobal = e;
      installListeners.forEach(cb => cb(true));
      if (!dismissed) setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Detect app already installed
    window.addEventListener('appinstalled', () => {
      deferredPromptGlobal = null;
      setShow(false);
      installListeners.forEach(cb => cb(false));
      // Track if not already tracked via triggerInstall
      try { axios.post(`${API}/settings/track-install`); } catch {}
    });

    // Check for service worker updates
    if ('serviceWorker' in navigator) {
      const lastUpdateTime = parseInt(localStorage.getItem('pwa-update-done') || '0', 10);
      const cooldownMs = 3600000; // 1 hour cooldown after update
      const inCooldown = Date.now() - lastUpdateTime < cooldownMs;

      navigator.serviceWorker.getRegistration().then(reg => {
        if (!reg) return;
        // Check for waiting worker (update ready) - skip if in cooldown
        if (reg.waiting && !inCooldown) {
          setUpdateAvailable(true);
          setNewWorker(reg.waiting);
        }
        // Listen for new updates
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller && !inCooldown) {
              setUpdateAvailable(true);
              setNewWorker(installing);
            }
          });
        });
      });

      // Reload on controller change only when the user explicitly asked for the update
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing && userRequestedUpdate) {
          refreshing = true;
          window.location.reload();
        }
      });
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    const accepted = await triggerInstall();
    if (accepted) setShow(false);
  };

  const handleUpdate = async () => {
    setUpdateLoading(true);
    userRequestedUpdate = true;
    localStorage.setItem('pwa-update-done', String(Date.now()));

    // 1) Send SKIP_WAITING to the new worker
    try {
      if (newWorker) newWorker.postMessage({ type: 'SKIP_WAITING' });
    } catch {}

    // 2) controllerchange will fire and reload. Fallback after 2s if nothing happens.
    setTimeout(async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch {}
      window.location.reload();
    }, 2000);
  };

  const handleDismissUpdate = () => {
    setUpdateAvailable(false);
    localStorage.setItem('pwa-update-done', String(Date.now()));
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('pwa-dismissed', '1');
  };

  // Show update prompt
  if (updateAvailable) {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] max-w-sm w-full mx-4" data-testid="pwa-update-prompt">
        <div className="bg-white rounded-2xl shadow-2xl border border-emerald-200 p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <RefreshCw size={22} className={`text-emerald-600 ${updateLoading ? 'animate-spin' : ''}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-sm">Mise à jour disponible</h3>
              <p className="text-xs text-gray-500 mt-1">Une nouvelle version de SFOALC est prête</p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={handleUpdate}
                  disabled={updateLoading}
                  className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                  data-testid="pwa-update-btn"
                >
                  <RefreshCw size={12} className={updateLoading ? 'animate-spin' : ''} />
                  {updateLoading ? 'Mise à jour…' : 'Mettre à jour'}
                </button>
                <button
                  onClick={handleDismissUpdate}
                  disabled={updateLoading}
                  className="px-4 py-2 text-gray-500 text-xs font-medium hover:text-gray-700 transition-colors disabled:opacity-50"
                  data-testid="pwa-update-dismiss-btn"
                >
                  Plus tard
                </button>
              </div>
            </div>
            <button onClick={handleDismissUpdate} disabled={updateLoading} className="text-gray-400 hover:text-gray-600 p-1 disabled:opacity-50" aria-label="Fermer">
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show install prompt
  if (!show) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] max-w-sm w-full mx-4 animate-in slide-in-from-bottom" data-testid="pwa-install-prompt">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#35AFE4]/10 flex items-center justify-center flex-shrink-0">
            <Download size={22} className="text-[#35AFE4]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Installer SFOALC</h3>
            <p className="text-xs text-gray-500 mt-1">Accédez rapidement à l'annuaire et vos formations depuis votre écran d'accueil</p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleInstall}
                className="px-4 py-2 bg-[#35AFE4] text-white text-xs font-bold rounded-lg hover:bg-[#2d7d9e] transition-colors"
                data-testid="pwa-install-btn"
              >
                Installer
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-gray-500 text-xs font-medium hover:text-gray-700 transition-colors"
                data-testid="pwa-dismiss-btn"
              >
                Plus tard
              </button>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
